import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { localStore } from "../../../lib/store";
import { apiClient } from "../../../services/api";
import { juisciMailboxBase } from "../../../../../api/src/shared/juisci-address";
import { displayToast } from "../../../components/app/AppToast";
import { ClientError } from "../../../../../api/src/shared/request";
import { t } from "i18next";
import i18n, { DEFAULT_LANGUAGE } from "../../../config/i18n";
import { parseSupportedLanguage } from "../../app/utils/langUtils";
import {
  gaEventOtpFailed,
  gaEventOtpRequested,
  gaEventOtpVerified,
  type AuthIntent,
  type OtpFailureReason,
  type SignupSurface,
} from "../../../tools/analytics/domains/onboarding.analytics";

/** Digits in a code. Mirrors `EMAIL_OTP_LENGTH` on the API. */
export const OTP_CODE_LENGTH = 6;

/** The screen's own "Resend code" countdown. The server enforces the same gap;
 * this one exists so the button says why it is disabled. */
const RESEND_COOLDOWN_SECONDS = 30;

/** How long a mailed code stays valid. Mirrors `EMAIL_OTP_TTL_MS` on the API. */
const OTP_CODE_TTL_MS = 10 * 60 * 1000;

/**
 * The code this device last had mailed, while it can still be typed.
 *
 * The reader leaves the app to read it. On Android that is exactly when the OS
 * reclaims the WebView renderer or the whole process, and either one wipes the
 * screen's memory, its `sessionStorage` and its router state: the app came back
 * on `/welcome`. Asking again then MAILED A NEW CODE, which kills the one in
 * the mailbox, so the reader typed a code the server had just retired.
 *
 * `localStorage`, because it is the one store that survives a rebuilt WebView
 * and a killed process. It holds the address and the intent, never the code:
 * nothing here signs anybody in. Expired entries read as absent.
 */
const pendingCodeSchema = z.object({
  email: z.string(),
  intent: z.enum(["login", "signup"]),
  requestedAt: z.number(),
});
const pendingCodeStore = localStore<z.infer<typeof pendingCodeSchema> | null>(
  "pendingEmailCode",
  null,
  pendingCodeSchema.nullable()
);

function livePendingCode(): z.infer<typeof pendingCodeSchema> | null {
  const pending = pendingCodeStore.get();
  if (pending === null) return null;
  if (Date.now() - pending.requestedAt >= OTP_CODE_TTL_MS) return null;
  return pending;
}

/** Forget the mailed code. Logout calls it: the next person on this device
 * must not land on somebody else's code screen. */
export function clearPendingEmailCode(): void {
  pendingCodeStore.set(null);
}

/** The code still waiting to be typed on this device, or `null`. */
export function readPendingEmailCode(): {
  email: string;
  intent: AuthIntent;
} | null {
  const pending = livePendingCode();
  if (pending === null) return null;
  return { email: pending.email, intent: pending.intent };
}

export type EmailOtpState =
  | { status: "idle" }
  | { status: "sending" }
  | { status: "awaiting_code"; error: OtpFailureReason | null }
  | { status: "verifying" };

/** What a proved address hands the caller. The two ways to prove one — a code,
 * or a trusted device — hand back exactly the same thing. */
export type EmailOtpCredential = {
  customToken: string;
  isNewAccount: boolean;
  uid: string;
};

type VerifyOutcome =
  | ({ outcome: "verified" } & EmailOtpCredential)
  | { outcome: "rejected"; reason: OtpFailureReason };

export type RequestOutcome =
  /** A code is on its way. The six boxes take over. */
  | { outcome: "code_sent" }
  /** No code left, and the screen already says why. */
  | { outcome: "not_sent" }
  /** This browser had already proved this mailbox. No code, no code screen:
   * the caller signs in with what it holds. */
  | ({ outcome: "trusted" } & EmailOtpCredential);

/**
 * The email-code exchange, shared by the signup slide and the login page.
 *
 * It owns the three things neither surface should re-implement: the resend
 * countdown, the attempt count that `otp_verified` reports, and the
 * request-to-acceptance latency that tells a deliverability problem from a user
 * one. Signing the returned custom token in is the CALLER's job — the two
 * surfaces go somewhere different afterwards.
 */
export function useEmailOtp(params: {
  intent: AuthIntent;
  surface: SignupSurface;
}): {
  state: EmailOtpState;
  cooldownRemaining: number;
  requestCode: (email: string) => Promise<RequestOutcome>;
  resumeCode: (email: string) => boolean;
  resendCode: (email: string) => Promise<boolean>;
  verifyCode: (email: string, code: string) => Promise<VerifyOutcome | null>;
  reset: () => void;
} {
  const { intent, surface } = params;
  const [state, setState] = useState<EmailOtpState>({ status: "idle" });
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  // Not state: nothing renders them, and a re-render on every keystroke of a
  // 6-box input would restart the countdown interval.
  const attemptsRef = useRef(0);
  const requestedAtRef = useRef<number | null>(null);
  // Bumped by `reset()`. A request still in flight when the reader backed out
  // must not reopen the screen, nor write back the record reset just dropped.
  const generationRef = useRef(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined
  );

  useEffect(() => {
    return () => {
      if (cooldownRef.current !== undefined) clearInterval(cooldownRef.current);
    };
  }, []);

  function startCooldown(seconds: number): void {
    setCooldownRemaining(seconds);
    if (cooldownRef.current !== undefined) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldownRemaining((remaining) => {
        if (remaining <= 1) {
          clearInterval(cooldownRef.current);
          cooldownRef.current = undefined;
          return 0;
        }
        return remaining - 1;
      });
    }, 1000);
  }

  async function send(email: string, isResend: boolean): Promise<boolean> {
    const generation = generationRef.current;
    setState({ status: "sending" });
    const resolved = i18n.resolvedLanguage;
    const parsed =
      resolved === undefined ? undefined : parseSupportedLanguage(resolved);
    const language = parsed === undefined ? DEFAULT_LANGUAGE : parsed;

    try {
      await apiClient.post("/webapp/public/auth/otp/request", {
        json: { email: email.trim().toLowerCase(), language, intent },
      });
    } catch (error) {
      if (generation !== generationRef.current) return false;
      if (error instanceof ClientError && error.responseStatus === 429) {
        gaEventOtpFailed({ reason: "rate_limited", intent });
        // Refused because a code went out moments ago. That is a REFUSAL only
        // when the user just pressed Resend; on the automatic first request it
        // is the ordinary answer to reloading the screen, and printing "too
        // many requests" over a mailbox that already holds a working code would
        // send the reader looking for a problem that is not there. Either way
        // the countdown is re-armed, because the server's gap is real.
        setState({
          status: "awaiting_code",
          error: isResend ? "rate_limited" : null,
        });
        startCooldown(RESEND_COOLDOWN_SECONDS);
        return false;
      }
      // Anything else is ours. Reported here rather than rethrown so the two
      // callers cannot each invent their own wording for it.
      displayToast(t("Something went wrong. Please try again."), "error");
      setState({ status: "idle" });
      return false;
    }

    gaEventOtpRequested({ intent, surface, is_resend: isResend });
    if (generation !== generationRef.current) return false;
    attemptsRef.current = 0;
    requestedAtRef.current = Date.now();
    pendingCodeStore.set({
      email: email.trim().toLowerCase(),
      intent,
      requestedAt: requestedAtRef.current,
    });
    setState({ status: "awaiting_code", error: null });
    startCooldown(RESEND_COOLDOWN_SECONDS);
    return true;
  }

  /**
   * Reopen the six boxes over a code this device already had mailed to this
   * address, instead of mailing another one. `false` when there is none left
   * to type.
   */
  function resumePendingCode(email: string): boolean {
    const pending = livePendingCode();
    if (pending === null) return false;
    if (pending.email !== email || pending.intent !== intent) return false;
    const elapsedMs = Date.now() - pending.requestedAt;
    attemptsRef.current = 0;
    requestedAtRef.current = pending.requestedAt;
    setState({ status: "awaiting_code", error: null });
    const cooldown = RESEND_COOLDOWN_SECONDS - Math.floor(elapsedMs / 1000);
    if (cooldown > 0) startCooldown(cooldown);
    return true;
  }

  /**
   * Try the trusted-device exemption. `null` means "ask for a code", for every
   * reason there is: no cookie, an expired one, one bound to another mailbox,
   * or a call that failed. A sign-in that cannot happen is never an error the
   * user should read — the code path is right there and it works.
   *
   * It emits none of the three OTP events on purpose. `otp_verified` carries an
   * attempt count and a mailbox latency, and there was neither.
   */
  async function trustedSignIn(
    email: string
  ): Promise<EmailOtpCredential | null> {
    try {
      const response = await apiClient.post(
        "/webapp/public/auth/otp/trusted-signin",
        // The device cookie is `httpOnly` on the API host, and the shared
        // client omits credentials by default (`shared/request.ts`).
        { json: { email, intent }, credentials: "include" }
      );
      const parsed = response.body;
      if (parsed.status === "untrusted") return null;
      return {
        customToken: parsed.customToken,
        isNewAccount: parsed.isNewAccount,
        uid: parsed.uid,
      };
    } catch {
      return null;
    }
  }

  /**
   * Ask for a code, unless this browser does not need one.
   *
   * A code this device already had mailed to this address, and that is still
   * valid, is resumed rather than replaced: a new one would kill it while the
   * reader is copying it out of their mailbox. Resend still mails a new one.
   *
   * The exemption is attempted FIRST, and only for an address it could cover.
   * That test is a string comparison and not a round-trip
   * (`juisciMailboxBase`), so a normal signup pays nothing for a feature
   * that is not for it.
   */
  async function requestCode(email: string): Promise<RequestOutcome> {
    const normalized = email.trim().toLowerCase();

    if (resumePendingCode(normalized)) return { outcome: "code_sent" };

    if (juisciMailboxBase(normalized) !== null) {
      const trusted = await trustedSignIn(normalized);
      if (trusted !== null) return { outcome: "trusted", ...trusted };
    }

    return (await send(normalized, false))
      ? { outcome: "code_sent" }
      : { outcome: "not_sent" };
  }

  async function resendCode(email: string): Promise<boolean> {
    if (cooldownRemaining > 0) return false;
    return send(email, true);
  }

  /**
   * Returns the outcome, or `null` when the call itself failed (a network
   * error, a 409 on an address that took an account meanwhile) — the caller
   * shows its own toast for that, because it is not a wrong code.
   */
  async function verifyCode(
    email: string,
    code: string
  ): Promise<VerifyOutcome | null> {
    setState({ status: "verifying" });
    attemptsRef.current += 1;

    let parsed;
    try {
      const response = await apiClient.post("/webapp/public/auth/otp/verify", {
        json: { email: email.trim().toLowerCase(), code, intent },
        // A verified code on a tagged staff address answers with a device
        // cookie. Without `include` the browser drops the `Set-Cookie` and the
        // exemption never starts.
        credentials: "include",
      });
      // No re-validation: the client is derived from the route's own return
      // types (`Architecture.md` "Local-first Typing"), so a Zod copy here would
      // be a second declaration of the same contract, free to drift.
      parsed = response.body;
    } catch (error) {
      setState({ status: "awaiting_code", error: null });
      if (error instanceof ClientError && error.responseStatus === 429) {
        gaEventOtpFailed({ reason: "rate_limited", intent });
        setState({ status: "awaiting_code", error: "rate_limited" });
        return { outcome: "rejected", reason: "rate_limited" };
      }
      return null;
    }

    if (parsed.status === "rejected") {
      // An expired or exhausted code cannot be typed any more, so a reload must
      // not reopen the boxes over it: it asks for a fresh one instead.
      const reason = parsed.reason;
      switch (reason) {
        case "expired":
        case "too_many_attempts":
          pendingCodeStore.set(null);
          break;
        case "wrong_code":
          break;
        default:
          throw new Error(reason satisfies never);
      }
      gaEventOtpFailed({ reason: parsed.reason, intent });
      setState({ status: "awaiting_code", error: parsed.reason });
      return { outcome: "rejected", reason: parsed.reason };
    }

    pendingCodeStore.set(null);
    const requestedAt = requestedAtRef.current;
    gaEventOtpVerified({
      intent,
      attempts: attemptsRef.current,
      latency_ms: requestedAt === null ? 0 : Date.now() - requestedAt,
    });
    return {
      outcome: "verified",
      customToken: parsed.customToken,
      isNewAccount: parsed.isNewAccount,
      uid: parsed.uid,
    };
  }

  function reset(): void {
    if (cooldownRef.current !== undefined) clearInterval(cooldownRef.current);
    cooldownRef.current = undefined;
    setCooldownRemaining(0);
    attemptsRef.current = 0;
    requestedAtRef.current = null;
    generationRef.current += 1;
    pendingCodeStore.set(null);
    setState({ status: "idle" });
  }

  return {
    state,
    cooldownRemaining,
    requestCode,
    resumeCode: resumePendingCode,
    resendCode,
    verifyCode,
    reset,
  };
}
